import { useState, useEffect, useRef } from "react";
import { Link as InertiaLink, useForm } from "@inertiajs/react";
import {
  Card,
  CardBody,
  CardHeader,
  Tabs,
  Tab,
  Textarea,
  Button,
  Chip,
  Link,
  Tooltip,
  Divider,
  Progress,
  addToast,
} from "@heroui/react";
import Layout from "../Layouts/Layout";
import { subscribeToRunUpdates } from "../../channels/run_channel";

export default function Show({ task, recent_runs }) {
  const [activeTab, setActiveTab] = useState("run");
  const [currentRuns, setCurrentRuns] = useState(recent_runs);
  const [inputError, setInputError] = useState("");
  const inputRef = useRef(null);

  const { data, setData, post, processing, reset } = useForm({
    input: "",
  });

  useEffect(() => {
    const subscription = subscribeToRunUpdates((updateData) => {
      setCurrentRuns((prevRuns) => {
        if (updateData.action === "created" && updateData.run) {
          if (updateData.run.task.id !== task.id) return prevRuns;
          const exists = prevRuns.some((run) => run.id === updateData.run.id);
          if (exists) return prevRuns;
          return [updateData.run, ...prevRuns];
        }

        const runExists = prevRuns.some(
          (run) => run.id === updateData.run_id
        );
        if (!runExists) return prevRuns;

        return prevRuns.map((run) =>
          run.id === updateData.run_id
            ? {
                ...run,
                status: updateData.status,
                output: updateData.output || run.output,
                error_message: updateData.error || run.error_message,
                input_tokens: updateData.tokens?.input || run.input_tokens,
                output_tokens: updateData.tokens?.output || run.output_tokens,
              }
            : run
        );
      });
    });

    return () => subscription.unsubscribe();
  }, [task.id]);

  const handleRunTask = (e) => {
    e.preventDefault();
    setInputError("");

    if (!data.input.trim()) {
      setInputError("Please enter some input to run the task");
      inputRef.current?.focus();
      return;
    }

    post(`/tasks/${task.id}/runs`, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        reset();
        setActiveTab("runs");
        if (typeof addToast === "function") {
          addToast({
            title: "Task submitted",
            description: "Your task is now processing",
            color: "success",
          });
        }
      },
      onError: (errors) => {
        const errorMessage = Object.values(errors).join(", ");
        setInputError(errorMessage);
        if (typeof addToast === "function") {
          addToast({
            title: "Failed to run task",
            description: errorMessage,
            color: "danger",
          });
        }
      },
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "danger";
      case "processing":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            as={InertiaLink}
            href="/tasks"
            className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors mb-4"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Tasks
          </Link>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {task.name}
                </h1>
                <Chip
                  color={
                    task.visibility === "public"
                      ? "success"
                      : task.visibility === "incognito"
                        ? "default"
                        : "primary"
                  }
                  variant="flat"
                  size="sm"
                >
                  {task.visibility}
                </Chip>
              </div>
              {task.description && (
                <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
                  {task.description}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                as={InertiaLink}
                href={`/tasks/${task.id}/edit`}
                variant="flat"
                startContent={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                }
              >
                Edit task
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={setActiveTab}
            aria-label="Task tabs"
            variant="underlined"
            classNames={{
              base: "w-full",
              tabList:
                "gap-6 w-full relative rounded-none p-0 px-6 border-b border-gray-200 dark:border-gray-700",
              cursor: "w-full bg-amber-500",
              tab: "max-w-fit px-0 h-12",
              tabContent:
                "text-gray-600 dark:text-gray-400 group-data-[selected=true]:text-amber-600 dark:group-data-[selected=true]:text-amber-400",
            }}
          >
            <Tab
              key="run"
              title={
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Run task
                </div>
              }
            >
              <CardBody className="p-6">
                <form onSubmit={handleRunTask} className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-semibold text-gray-900 dark:text-white">
                        Input <span className="text-red-500">*</span>
                      </label>
                      <span className="text-xs text-gray-500">
                        {data.input.length} characters
                      </span>
                    </div>
                    <Textarea
                      ref={inputRef}
                      placeholder="Enter your input here..."
                      value={data.input}
                      onChange={(e) => {
                        setData("input", e.target.value);
                        if (inputError) setInputError("");
                      }}
                      minRows={8}
                      variant="bordered"
                      classNames={{
                        inputWrapper: inputError
                          ? "border-red-500 dark:border-red-500"
                          : "border-gray-200 dark:border-gray-700",
                      }}
                      isInvalid={!!inputError}
                      errorMessage={inputError}
                    />
                  </div>

                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="text-sm">
                        <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
                          Task configuration
                        </p>
                        <div className="flex flex-wrap gap-2 text-amber-700 dark:text-amber-400">
                          <span>Model: {task.selected_model.display_name || "Default"}</span>
                          <span>|</span>
                          <span>
                            Format:{" "}
                            {task.output_format?.replace("_", " ") ||
                              "Plain text"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    isLoading={processing}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
                    startContent={
                      !processing && (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      )
                    }
                  >
                    {processing ? "Processing..." : "Run task"}
                  </Button>
                </form>
              </CardBody>
            </Tab>

            <Tab
              key="details"
              title={
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Configuration
                </div>
              }
            >
              <CardBody className="p-6">
                <div className="space-y-6">
                  {/* Instructions */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        Instructions
                      </h3>
                      <Tooltip content="Copy instructions">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={() =>
                            copyToClipboard(
                              task.instructions || "No instructions"
                            )
                          }
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </Button>
                      </Tooltip>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono">
                      {task.instructions || "No instructions provided"}
                    </div>
                  </div>

                  {task.rules && (
                    <>
                      <Divider />
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                            Rules
                          </h3>
                          <Tooltip content="Copy rules">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() => copyToClipboard(task.rules)}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </Button>
                          </Tooltip>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                          {task.rules}
                        </div>
                      </div>
                    </>
                  )}

                  {task.examples && task.examples.length > 0 && (
                    <>
                      <Divider />
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-3">
                          Examples ({task.examples.length})
                        </h3>
                        <div className="space-y-3">
                          {task.examples.map((example, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4"
                            >
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Example {idx + 1}
                                </span>
                                <Tooltip content="Copy example">
                                  <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    onPress={() => copyToClipboard(example)}
                                  >
                                    <svg
                                      className="w-3.5 h-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </Button>
                                </Tooltip>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {example}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <Divider />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Output Format
                      </h4>
                      <Chip variant="flat" color="warning" size="sm">
                        {task.output_format
                          ? task.output_format
                              .replace("_", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())
                          : "Plain Text"}
                      </Chip>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Model
                      </h4>
                      <Chip variant="flat" color="primary" size="sm">
                        {task.selected_model.display_name || "Default"}
                      </Chip>
                    </div>
                  </div>

                  {task.output_schema && (
                    <>
                      <Divider />
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                            Output Schema
                          </h3>
                          <Tooltip content="Copy schema">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() =>
                                copyToClipboard(
                                  JSON.stringify(task.output_schema, null, 2)
                                )
                              }
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </Button>
                          </Tooltip>
                        </div>
                        <pre className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 overflow-x-auto text-sm font-mono text-gray-700 dark:text-gray-300">
                          {JSON.stringify(task.output_schema, null, 2)}
                        </pre>
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Tab>

            <Tab
              key="runs"
              title={
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  History
                  <Chip size="sm" variant="flat">
                    {currentRuns.length}
                  </Chip>
                </div>
              }
            >
              <CardBody className="p-6">
                {currentRuns.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      No runs yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Execute this task to see your run history here
                    </p>
                    <Button
                      variant="flat"
                      color="warning"
                      onPress={() => setActiveTab("run")}
                    >
                      Run task now
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {currentRuns.map((run) => (
                      <Card
                        key={run.id}
                        className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200/50 dark:border-gray-700/50"
                      >
                        <CardBody className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Chip
                                color={getStatusColor(run.status)}
                                variant="flat"
                                size="sm"
                                startContent={
                                  run.status === "processing" ? (
                                    <span className="w-2 h-2 bg-current rounded-full animate-pulse" />
                                  ) : null
                                }
                              >
                                {run.status}
                              </Chip>
                              {run.input_tokens && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {(
                                    run.input_tokens + run.output_tokens
                                  ).toLocaleString()}{" "}
                                  tokens
                                </span>
                              )}
                            </div>
                            <Tooltip
                              content={new Date(run.created_at).toLocaleString()}
                            >
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(run.created_at)}
                              </span>
                            </Tooltip>
                          </div>

                          {run.status === "processing" && (
                            <Progress
                              size="sm"
                              isIndeterminate
                              color="warning"
                              className="mb-3"
                            />
                          )}

                          <div className="space-y-3">
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Input
                                </p>
                                <Tooltip content="Copy input">
                                  <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    onPress={() => copyToClipboard(run.input)}
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </Button>
                                </Tooltip>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 font-mono bg-white dark:bg-gray-800 rounded p-2">
                                {run.input}
                              </p>
                            </div>

                            {run.status === "completed" && run.output && (
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                                    Output
                                  </p>
                                  <Tooltip content="Copy output">
                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                      onPress={() => copyToClipboard(run.output)}
                                    >
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                      </svg>
                                    </Button>
                                  </Tooltip>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 font-mono bg-green-50 dark:bg-green-900/20 rounded p-2">
                                  {run.output}
                                </p>
                              </div>
                            )}

                            {run.status === "failed" && run.error_message && (
                              <div>
                                <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider mb-1">
                                  Error
                                </p>
                                <p className="text-sm text-red-700 dark:text-red-300 font-mono bg-red-50 dark:bg-red-900/20 rounded p-2">
                                  {run.error_message}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-700/50">
                            <Button
                              as={InertiaLink}
                              href={`/runs/${run.id}`}
                              variant="light"
                              color="warning"
                              size="sm"
                              endContent={
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              }
                            >
                              View full details
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </CardBody>
            </Tab>
          </Tabs>
        </Card>
      </div>
    </Layout>
  );
}
